import { useContext, useRef, useEffect, useState, useLayoutEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { ProgramContext } from 'context/ProgramContext'

import MesoBlockLengthSelector from 'components/Program/MesoVolumeConfig/MesoBlockLengthSelector'
import CreateMicroVolumeConfigButton from 'components/Program/MicroVolumeConfig/CreateMicroVolumeConfigButton'
import MicroVolumeConfigBar from 'components/Program/MicroVolumeConfig/MicroVolumeConfigBar'

export default function MesoVolumeConfigDisplay({ 
    programMetadata = null,
    mode = 'view',
    enableAnimation = true,
    editHeight = 900,
    mesoVolumeConfig,
    parameterMacroVolumeFocusTypeHashMap = null,
    parameterMicroVolumeStimulusTypeHashMap = null,
    selectedMicroVolumeConfigIndex: controlledSelectedIndex,
    setSelectedMicroVolumeConfigIndex: controlledSetSelectedIndex,
    HandleMesoVolumeConfigDraftChange = () => {}
}) {

    var macroVolumeFocusTypeHashMap = parameterMacroVolumeFocusTypeHashMap
    if (macroVolumeFocusTypeHashMap === null) macroVolumeFocusTypeHashMap = programMetadata?.macroVolumeFocusTypeHashMap
    var microVolumeStimulusTypeHashMap = parameterMicroVolumeStimulusTypeHashMap
    if (microVolumeStimulusTypeHashMap === null) microVolumeStimulusTypeHashMap = programMetadata?.microVolumeStimulusTypeHashMap

    const { MicroVolumeConfigSubContextData } = useContext(ProgramContext)
    const { GetMicroVolumeConfigRepsCount, GetMicroVolumeConfigSetCount } = MicroVolumeConfigSubContextData

    const [selectedBlockLength, setSelectedBlockLength] = useState(4)
    const [internalSelectedIndex, setInternalSelectedIndex] = useState(null)
    
    const selectedMicroVolumeConfigIndex = controlledSetSelectedIndex ? controlledSelectedIndex : internalSelectedIndex
    const setSelectedMicroVolumeConfigIndex = controlledSetSelectedIndex ?? setInternalSelectedIndex

    const scrollContainerRef = useRef(null)
    const weekBarRefs = useRef([])
    const previousSelectedIndexRef = useRef(null)

    const viewMeasureRef = useRef(null)
    const viewContentRef = useRef(null)
    const [viewScale, setViewScale] = useState(1)
    const [fittedEditHeight, setFittedEditHeight] = useState(null)

    const DoesMicroVolumeSupportBlockLength = (microVolumeConfig, blockLength) => {
        const supportedBlockLengths = microVolumeConfig?.supportedBlockLengthsJSON

        if (!Array.isArray(supportedBlockLengths) || supportedBlockLengths.length === 0) {
            return true
        }

        return supportedBlockLengths.includes(blockLength)
    }

    const visibleMicroVolumes = (mesoVolumeConfig?.microVolumes ?? []).filter((microVolumeConfig) => (
        DoesMicroVolumeSupportBlockLength(microVolumeConfig, selectedBlockLength)
    ))

    const CenterMesoConfigBlock = (behavior = 'smooth') => {
        const scrollContainer = scrollContainerRef.current

        if (scrollContainer === null) return

        scrollContainer.scrollTo({
            left: (scrollContainer.scrollWidth - scrollContainer.clientWidth) / 2,
            behavior: behavior,
        })
    }

    const CenterMicroVolumeConfigWeekBar = (index, behavior = 'smooth') => {
        const weekBar = weekBarRefs.current[index]
        const scrollContainer = scrollContainerRef.current

        if (weekBar === null || weekBar === undefined || scrollContainer === null) return

        const barRect = weekBar.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()

        const barCenter = barRect.left + (barRect.width / 2)
        const containerCenter = containerRect.left + (containerRect.width / 2)

        scrollContainer.scrollTo({
            left: scrollContainer.scrollLeft + (barCenter - containerCenter),
            behavior: behavior,
        })
    }

    const ToggleSelectedMicroVolumeConfig = (index) => {
        setSelectedMicroVolumeConfigIndex(index === selectedMicroVolumeConfigIndex ? null : index)
    }

    const AddMicroVolumeConfigToStart = () => {
        HandleMesoVolumeConfigDraftChange('add-new-micro-volume-config-to-start')
        setSelectedMicroVolumeConfigIndex(0)
    }

    const AddMicroVolumeConfigToEnd = () => {
        const newIndex = mesoVolumeConfig.microVolumes.length
        HandleMesoVolumeConfigDraftChange('add-new-micro-volume-config-to-end')
        setSelectedMicroVolumeConfigIndex(newIndex)
    }

    const DuplicateMicroVolumeConfig = (index) => {
        HandleMesoVolumeConfigDraftChange('duplicate-micro-volume-config', index)
        setSelectedMicroVolumeConfigIndex(index + 1)
    }

    const DeleteMicroVolumeConfig = (index) => {
        const remainingCount = mesoVolumeConfig.microVolumes.length - 1

        HandleMesoVolumeConfigDraftChange('delete-micro-volume-config', index)

        if (remainingCount === 0) {
            setSelectedMicroVolumeConfigIndex(null)
        } else {
            setSelectedMicroVolumeConfigIndex(index < remainingCount ? index : index - 1)
        }
    }

    useEffect(() => { CenterMesoConfigBlock('auto')}, [])

    useEffect(() => {
        const previousSelectedIndex = previousSelectedIndexRef.current
        previousSelectedIndexRef.current = selectedMicroVolumeConfigIndex

        if (selectedMicroVolumeConfigIndex !== null) {
            CenterMicroVolumeConfigWeekBar(selectedMicroVolumeConfigIndex)
        } else if (previousSelectedIndex !== null) {
            CenterMesoConfigBlock()
        }
    }, [selectedMicroVolumeConfigIndex, mesoVolumeConfig?.microVolumes?.length])

    useLayoutEffect(() => {
        if (mode !== 'edit' || editHeight !== 'auto') return

        const element = scrollContainerRef.current
        if (element === null) return

        const FitEditHeight = () => {
            const nextHeight = Math.ceil(element.scrollHeight)

            setFittedEditHeight((current) => {
                if (current != null && nextHeight <= current + 1) return current
                return nextHeight
            })
        }

        FitEditHeight()

        const resizeObserver = new ResizeObserver(FitEditHeight)
        resizeObserver.observe(element)

        return () => resizeObserver.disconnect()
    }, [mode, editHeight, mesoVolumeConfig])

    useLayoutEffect(() => {
        if (mode !== 'view') return

        const RecomputeViewScale = () => {
            const measureElement = viewMeasureRef.current
            const contentElement = viewContentRef.current

            if (measureElement === null || contentElement === null) return

            const availableWidth = measureElement.clientWidth
            const naturalWidth = contentElement.scrollWidth

            if (naturalWidth === 0) return

            setViewScale(Math.min(1, availableWidth / naturalWidth))
        }

        RecomputeViewScale()

        const resizeObserver = new ResizeObserver(RecomputeViewScale)
        resizeObserver.observe(viewMeasureRef.current)
        resizeObserver.observe(viewContentRef.current)

        return () => resizeObserver.disconnect()
    }, [mode, visibleMicroVolumes.length, selectedBlockLength])

    if (mode === 'view') {
        return (
            <div className="relative p-4 bg-gradient-to-b from-zinc-800 to-zinc-600 rounded-xl overflow-hidden">
                {mesoVolumeConfig?.microVolumes.length > 0 &&
                    <div className="absolute top-4 left-1/2 z-[1] -translate-x-1/2">
                        <MesoBlockLengthSelector
                            selectedLength={selectedBlockLength}
                            setSelectedLength={setSelectedBlockLength}
                        />
                    </div>
                }

                <div 
                    ref={viewMeasureRef} 
                    className="w-full flex items-center justify-center"
                >
                    <div
                        ref={viewContentRef}
                        className="flex gap-x-1"
                        style={{ 
                            transform: `scale(${viewScale})`, 
                            transformOrigin: 'center center' 
                        }}
                    >
                        {visibleMicroVolumes.length === 0 &&
                            <p className='h-32 text-sm text-center text-gray-400 flex items-center justify-center'>
                                Empty
                            </p>
                        }

                        {visibleMicroVolumes.length > 0 && visibleMicroVolumes.map((microVolumeConfig, index) => (
                            <div 
                                key={microVolumeConfig.microVolumeId ?? index}
                                className='pt-10 space-y-2'
                            >
                                <MicroVolumeConfigBar
                                    index={index}
                                    enableAnimation={false}
                                    macroVolumeFocusTypeId={mesoVolumeConfig.macroVolumeFocusTypeId}
                                    macroVolumeFocusTypeHashMap={macroVolumeFocusTypeHashMap}
                                    microVolumeConfig={microVolumeConfig}
                                    microVolumeStimulusTypeHashMap={microVolumeStimulusTypeHashMap}
                                />

                                <div className='space-y-1'>
                                    <div className='text-white text-center'>
                                        <p className='font-bold'>{GetMicroVolumeConfigSetCount(microVolumeConfig)}</p>
                                        <p className='-mt-1 text-xxs'>sets</p>
                                    </div>

                                    <div className='text-white text-center'>
                                        <p className='font-bold'>{GetMicroVolumeConfigRepsCount(microVolumeConfig)}</p>
                                        <p className='-mt-1 text-xxs'>reps</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (mode === 'edit') {
        const microVolumeBars = mesoVolumeConfig.microVolumes.map((microVolumeConfig, index) => (
            <MicroVolumeConfigBar
                key={microVolumeConfig._animationUUID ?? index}
                ref={(element) => { weekBarRefs.current[index] = element }}
                enableAnimation={enableAnimation}
                variant='edit'
                microVolumeConfigIndex={index}
                isSelected={index === selectedMicroVolumeConfigIndex}
                macroVolumeFocusTypeId={mesoVolumeConfig.macroVolumeFocusTypeId}
                macroVolumeFocusTypeHashMap={macroVolumeFocusTypeHashMap}
                microVolumeConfig={microVolumeConfig}
                microVolumeStimulusTypeHashMap={microVolumeStimulusTypeHashMap}
                onClick={() => ToggleSelectedMicroVolumeConfig(index)}
                onDuplicate={() => DuplicateMicroVolumeConfig(index)}
                onDelete={() => DeleteMicroVolumeConfig(index)}
            />
        ))

        return (
            <div 
                ref={scrollContainerRef}
                className="relative p-4 bg-gradient-to-b from-zinc-800 to-zinc-600 rounded-xl flex items-start gap-x-4 overflow-auto [scrollbar-width:thin]"
                style={{
                    height: editHeight === 'auto'
                        ? (fittedEditHeight ?? 'max-content')
                        : editHeight,
                }}
            >
                {mesoVolumeConfig.microVolumes.length === 0 &&        
                    <div className='w-full mx-auto flex justify-center'>            
                        <CreateMicroVolumeConfigButton
                            variant='edit'
                            onClick={AddMicroVolumeConfigToStart}
                        />
                    </div>
                }

                {mesoVolumeConfig.microVolumes.length > 0 &&
                    <>
                        <div className="shrink-0 w-[calc(50%-10rem)]" aria-hidden="true" />

                        <CreateMicroVolumeConfigButton
                            variant='edit'
                            onClick={AddMicroVolumeConfigToStart}
                        />

                        {enableAnimation
                            ? <AnimatePresence initial={false}>{microVolumeBars}</AnimatePresence>
                            : microVolumeBars
                        }

                        <CreateMicroVolumeConfigButton
                            variant='edit'
                            onClick={AddMicroVolumeConfigToEnd}
                        />

                        <div className="shrink-0 w-[calc(50%-10rem)]" aria-hidden="true" />
                    </>
                }
            </div>
        )
    }
    
    return null
}
